import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize'
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import { groupSubjectData } from '../utils/teachingUtils';

// การ์ดรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const AttendanceLinkCard = ({ item }) => (
  <div className="border rounded p-3">
    <div className="fw-semibold">ม.{item.tb_class_c_id}/{item.r_no} · {item.sub_subname}</div>
    <div className="fs-xs text-muted">อาจารย์ที่ปรึกษา: {item.advisor_name}</div>
    <div className="mt-2 pt-2 border-top">
      <Link to={`/teaching/list-attendance/${item.teach_id}`} className="btn btn-outline-primary btn-sm w-100">
        <i className="ri-contacts-book-line me-1" />แสดงการเช็คชื่อ
      </Link>
    </div>
  </div>
)

export default function ManageAttendance() {
  const { setLoading } = useLoading();

  // Skin Switch
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }

    // Toggle dark mode for all tables
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  };

  switchSkin(skin);

  useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  // จัดการ State สำหรับปีการศึกษาและข้อมูลที่โหลดได้
  const [yearList, setYearList] = useState([])
  const [yearId, setYearId] = useState(() => {
    return localStorage.getItem('yearId') || null
  })

  const [selectedYear, setSelectedYear] = useState("")
  const [data, setData] = useState([]);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  // ดึงรายการปีการศึกษา
  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/getStudyYear`, {
        headers: {
          authorization: `Bearer ${getToken()}`
        }
      })
      .then((res) => {
        const list = res.data;
        setYearList(list || []);
       // ถ้า yearId ยังไม่มีใน localStorage ให้ใช้ปีล่าสุด
      let initialYearId = localStorage.getItem('yearId');
        if (!initialYearId && list.length > 0) {
          initialYearId = list[0].year_id;
          localStorage.setItem('yearId', initialYearId);
        }
        setYearId(initialYearId);
        const selected = list.find(y => String(y.year_id) === String(initialYearId));
        setSelectedYear(selected ? `ปีการศึกษา ${selected.year_year}` : "");
      })
      .catch(err => {
        console.log("Error loading years:", err);
      })
      .finally(() => {
        setLoading(false);
      })
  }, [setLoading])

  // เมื่อ yearId เปลี่ยน ให้โหลดข้อมูลการเช็คชื่อ/รายวิชา
  useEffect(() => {
    if (!yearId) return;
    setLoading(true);

    // ใช้ endpoint เดียวกับที่หน้า SubjectClass.js ใช้
    // (ปรับตามโค้ดจริงว่าต้องการดึงข้อมูลแบบใด หากต้องดึงตามปีการศึกษา)
    axiosInstance
      .get(`${process.env.REACT_APP_API}/getSubjectTeaching/${getUserId()}/${yearId}`, {
        headers: {
          authorization: `Bearer ${getToken()}`
        }
      })
      .then((response) => {
        setData(groupSubjectData(response.data) || []);
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          title: err.response?.statusText || "Error",
          text: err.message,
          icon: "error"
        });
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [yearId, setLoading]);

  // Handle การเปลี่ยนปีการศึกษาจาก Dropdown
  const handleSelectYear = (id) => {
    const selected = yearList.find(y => y.year_id === id);
    if (selected) {
      setYearId(id);
      setSelectedYear(`ปีการศึกษา ${selected.year_year}`);
      localStorage.setItem('yearId', id); // <-- เก็บลง LocalStorage
    }
  }

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="#">ข้อมูลการสอน</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                ข้อมูลการเช็คชื่อ
              </li>
            </ol>
            <h4 className="main-title mb-0">ข้อมูลการเช็คชื่อ</h4>
          </div>
          {/* Dropdown เลือกปีการศึกษา */}
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
              <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                onClick={() => setView('table')} title="มุมมองตาราง">
                <i className="ri-table-line" />
              </button>
              <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                onClick={() => setView('card')} title="มุมมองการ์ด">
                <i className="ri-layout-grid-line" />
              </button>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-basic">
                {selectedYear || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {yearList.map((item) => (
                  <Dropdown.Item
                    key={item.year_id}
                    onClick={() => handleSelectYear(item.year_id)}
                  >
                    {`ปีการศึกษา ${item.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="6">
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6">{`ภาคเรียนที่ 1 ${selectedYear || ""}`}</Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {view === 'card' ? (
                  <Row className="g-3">
                    {data && data.length > 0 && data.some(d => d.sub_semester === '1') ? (
                      data.filter(d => d.sub_semester === '1').map(item => (
                        <Col key={item.teach_id} xs={12}>
                          <AttendanceLinkCard item={item} />
                        </Col>
                      ))
                    ) : (
                      <Col xs={12}><div className="text-center text-muted py-3">ไม่พบข้อมูล</div></Col>
                    )}
                  </Row>
                ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr className="text-center">
                      <th scope="row" colSpan={4}>ภาคเรียนที่ 1</th>
                    </tr>
                  </thead>
                  <thead>
                    <tr>
                      <th scope="col" className="text-center" style={{ width: "70px" }}>
                        ชั้น
                      </th>
                      <th scope="col" className="text-center">
                        วิชา
                      </th>
                      <th scope="col" className="text-center">
                        อาจารย์ที่ปรึกษา
                      </th>
                      <th scope="col" className="text-center" style={{ width: "130px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data && data.length > 0 && data.some(d => d.sub_semester === '1') ? (
                      data
                        .filter(d => d.sub_semester === '1')
                        .map((item, index) => (
                          <tr key={item.teach_id}>
                            <td className="text-center">{item.tb_class_c_id}/{item.r_no}</td>
                            <td className="text-center">{item.sub_subname}</td>
                            <td className="text-center">{item.advisor_name}</td>
                            <td className="text-center">
                              <Link to={`/teaching/list-attendance/${item.teach_id}`}>
                                <i className="ri-contacts-book-line"> แสดงการเช็คชื่อ</i>
                              </Link>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6">{`ภาคเรียนที่ 2 ${selectedYear || ""}`}</Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {view === 'card' ? (
                  <Row className="g-3">
                    {data && data.length > 0 && data.some(d => d.sub_semester === '2') ? (
                      data.filter(d => d.sub_semester === '2').map(item => (
                        <Col key={item.teach_id} xs={12}>
                          <AttendanceLinkCard item={item} />
                        </Col>
                      ))
                    ) : (
                      <Col xs={12}><div className="text-center text-muted py-3">ไม่พบข้อมูล</div></Col>
                    )}
                  </Row>
                ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr className="text-center">
                      <th scope="row" colSpan={4}>ภาคเรียนที่ 2</th>
                    </tr>
                  </thead>
                  <thead>
                    <tr>
                      <th scope="col" className="text-center" style={{ width: "70px" }}>
                        ชั้น
                      </th>
                      <th scope="col" className="text-center">
                        วิชา
                      </th>
                      <th scope="col" className="text-center">
                        อาจารย์ที่ปรึกษา
                      </th>
                      <th scope="col" className="text-center" style={{ width: "130px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data && data.length > 0 && data.some(d => d.sub_semester === '2') ? (
                      data
                        .filter(d => d.sub_semester === '2')
                        .map((item, index) => (
                          <tr key={item.teach_id}>
                            <td className="text-center">{item.tb_class_c_id}/{item.r_no}</td>
                            <td className="text-center">{item.sub_subname}</td>
                            <td className="text-center">{item.advisor_name}</td>
                            <td className="text-center">
                              <Link to={`/teaching/list-attendance/${item.teach_id}`}>
                                <i className="ri-contacts-book-line"> แสดงการเช็คชื่อ</i>
                              </Link>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}